import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import ActionMenu from '@/components/base/ActionMenu';

interface WebhookLogRow {
  id: string;
  instance_name: string;
  event_type: string;
  event_data: Record<string, unknown> | null;
  phone_number: string | null;
  message_id: string | null;
  status: string | null;
  created_at: string;
}

const logCategories = [
  { key: 'all', label: 'Todos', icon: 'ri-file-list-3-line' },
  { key: 'sistema', label: 'Sistema', icon: 'ri-settings-3-line' },
  { key: 'campanhas', label: 'Campanhas', icon: 'ri-megaphone-line' },
  { key: 'backups', label: 'Backups', icon: 'ri-archive-line' },
  { key: 'atualizacoes', label: 'Atualizações', icon: 'ri-download-cloud-2-line' },
  { key: 'erros', label: 'Erros', icon: 'ri-error-warning-line' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line' },
  { key: 'login', label: 'Login', icon: 'ri-login-box-line' },
  { key: 'api', label: 'API', icon: 'ri-terminal-box-line' },
];

const actionColors: Record<string, string> = {
  'Campanha iniciada': 'bg-accent-100 text-accent-700',
  'Template criado': 'bg-secondary-100 text-secondary-700',
  'Contatos importados': 'bg-green-100 text-green-700',
  'Configuração WhatsApp': 'bg-primary-100 text-primary-700',
  'Campanha pausada': 'bg-amber-100 text-amber-700',
  'Grupo criado': 'bg-secondary-100 text-secondary-700',
  'Login': 'bg-background-200 text-foreground-600',
  'Campanha finalizada': 'bg-green-100 text-green-700',
  'Contato editado': 'bg-secondary-100 text-secondary-700',
  'Template excluído': 'bg-red-100 text-red-700',
  'Exportação': 'bg-background-200 text-foreground-600',
  'Webhook configurado': 'bg-primary-100 text-primary-700',
  'Campanha agendada': 'bg-accent-100 text-accent-700',
  'Conta criada': 'bg-green-100 text-green-700',
  'Falha de envio': 'bg-red-100 text-red-700',
  'Backup criado': 'bg-emerald-100 text-emerald-700',
  'Atualização aplicada': 'bg-primary-100 text-primary-700',
  'Erro do sistema': 'bg-red-100 text-red-700',
  'API Key gerada': 'bg-accent-100 text-accent-700',
};

const actionIcons: Record<string, string> = {
  'Campanha iniciada': 'ri-play-circle-line',
  'Template criado': 'ri-file-add-line',
  'Contatos importados': 'ri-upload-cloud-2-line',
  'Configuração WhatsApp': 'ri-whatsapp-line',
  'Campanha pausada': 'ri-pause-circle-line',
  'Grupo criado': 'ri-folder-add-line',
  'Login': 'ri-login-box-line',
  'Campanha finalizada': 'ri-check-double-line',
  'Contato editado': 'ri-user-settings-line',
  'Template excluído': 'ri-delete-bin-line',
  'Exportação': 'ri-download-2-line',
  'Webhook configurado': 'ri-plug-line',
  'Campanha agendada': 'ri-calendar-check-line',
  'Conta criada': 'ri-user-add-line',
  'Falha de envio': 'ri-error-warning-line',
  'Backup criado': 'ri-archive-line',
  'Atualização aplicada': 'ri-download-cloud-2-line',
  'Erro do sistema': 'ri-error-warning-line',
  'API Key gerada': 'ri-key-2-line',
};

const webhookEventColors: Record<string, string> = {
  'messages.upsert': 'bg-primary-50 text-primary-700 border-primary-200',
  'message': 'bg-primary-50 text-primary-700 border-primary-200',
  'qrcode.updated': 'bg-accent-50 text-accent-700 border-accent-200',
  'connection.update': 'bg-amber-50 text-amber-700 border-amber-200',
  'status': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'SENT': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'DELIVERED': 'bg-primary-50 text-primary-700 border-primary-200',
  'READ': 'bg-accent-50 text-accent-700 border-accent-200',
  'FAILED': 'bg-red-50 text-red-700 border-red-200',
};

const webhookEventIcon: Record<string, string> = {
  'messages.upsert': 'ri-chat-3-line', 'message': 'ri-chat-3-line',
  'qrcode.updated': 'ri-qr-code-line', 'connection.update': 'ri-plug-line',
  'status': 'ri-check-double-line',
  'SENT': 'ri-send-plane-fill', 'DELIVERED': 'ri-check-double-fill',
  'READ': 'ri-eye-fill', 'FAILED': 'ri-close-circle-fill',
};

const webhookStatusLabel: Record<string, string> = {
  'SENT': 'Enviado', 'DELIVERED': 'Entregue', 'READ': 'Lido', 'FAILED': 'Falhou',
  'sent': 'Enviado', 'delivered': 'Entregue', 'read': 'Lido', 'failed': 'Falhou',
};

// Mock logs with categories
const mockLogs = [
  { id: '1', action: 'Login', user: 'nx3designer@gmail.com', details: 'Login realizado com sucesso', ip: '189.45.32.10', created_at: new Date(Date.now() - 60000).toISOString(), category: 'login' },
  { id: '2', action: 'Campanha iniciada', user: 'nx3designer@gmail.com', details: 'Campanha "Promoção Verão" iniciada', ip: '189.45.32.10', created_at: new Date(Date.now() - 300000).toISOString(), category: 'campanhas' },
  { id: '3', action: 'Backup criado', user: 'Sistema', details: 'Backup automático diário concluído', ip: '127.0.0.1', created_at: new Date(Date.now() - 600000).toISOString(), category: 'backups' },
  { id: '4', action: 'Erro do sistema', user: 'Sistema', details: 'Timeout na conexão Redis após 30s', ip: '127.0.0.1', created_at: new Date(Date.now() - 900000).toISOString(), category: 'erros' },
  { id: '5', action: 'Configuração WhatsApp', user: 'admin@wzap.pro', details: 'Instância "Atendimento" conectada', ip: '201.17.45.22', created_at: new Date(Date.now() - 1200000).toISOString(), category: 'whatsapp' },
  { id: '6', action: 'Atualização aplicada', user: 'Sistema', details: 'Redis atualizado v7.2.4 → v7.2.5', ip: '127.0.0.1', created_at: new Date(Date.now() - 1800000).toISOString(), category: 'atualizacoes' },
  { id: '7', action: 'API Key gerada', user: 'nx3designer@gmail.com', details: 'Nova API Key gerada para integração', ip: '189.45.32.10', created_at: new Date(Date.now() - 2400000).toISOString(), category: 'api' },
  { id: '8', action: 'Contatos importados', user: 'admin@wzap.pro', details: '1.200 contatos importados via CSV', ip: '201.17.45.22', created_at: new Date(Date.now() - 3000000).toISOString(), category: 'sistema' },
  { id: '9', action: 'Falha de envio', user: 'Sistema', details: '3 mensagens falharam: número inválido', ip: '127.0.0.1', created_at: new Date(Date.now() - 3600000).toISOString(), category: 'erros' },
  { id: '10', action: 'Login', user: 'cliente@empresa.com', details: 'Login falhou: senha incorreta', ip: '45.178.22.90', created_at: new Date(Date.now() - 4200000).toISOString(), category: 'login' },
  { id: '11', action: 'Campanha finalizada', user: 'nx3designer@gmail.com', details: 'Campanha "Black Friday" finalizada: 8.450 enviadas', ip: '189.45.32.10', created_at: new Date(Date.now() - 4800000).toISOString(), category: 'campanhas' },
  { id: '12', action: 'Exportação', user: 'admin@wzap.pro', details: 'Relatório CSV exportado', ip: '201.17.45.22', created_at: new Date(Date.now() - 5400000).toISOString(), category: 'sistema' },
];

const categoryFilter = (category: string) => {
  if (category === 'all') return () => true;
  return (log: typeof mockLogs[0]) => log.category === category;
};

export default function LogsPage() {
  const { profile } = useAuth();
  const [tab, setTab] = useState<'system' | 'webhook'>('system');
  const [category, setCategory] = useState('all');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const [webhookLogs, setWebhookLogs] = useState<WebhookLogRow[]>([]);
  const [loadingWebhook, setLoadingWebhook] = useState(false);
  const [webhookPage, setWebhookPage] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const webhookPerPage = 15;

  const fetchWebhookLogs = useCallback(async () => {
    if (!profile?.company_id) return;
    setLoadingWebhook(true);
    const { data, error } = await supabase
      .from('evolution_webhook_logs')
      .select('*')
      .eq('company_id', profile.company_id)
      .order('created_at', { ascending: false })
      .limit(100);
    if (!error && data) setWebhookLogs(data as WebhookLogRow[]);
    setLoadingWebhook(false);
  }, [profile?.company_id]);

  useEffect(() => {
    if (tab === 'webhook') {
      fetchWebhookLogs();
      intervalRef.current = setInterval(() => { if (autoRefresh) fetchWebhookLogs(); }, 3000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [tab, autoRefresh, fetchWebhookLogs]);

  const filtered = mockLogs
    .filter(categoryFilter(category))
    .filter((l) => filter ? l.action.toLowerCase().includes(filter.toLowerCase()) || l.user.toLowerCase().includes(filter.toLowerCase()) : true);
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const webhookTotalPages = Math.ceil(webhookLogs.length / webhookPerPage);
  const paginatedWebhook = webhookLogs.slice((webhookPage - 1) * webhookPerPage, webhookPage * webhookPerPage);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {});
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const formatShortDate = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const whSent = webhookLogs.filter((l) => l.status === 'SENT' || l.status === 'sent').length;
  const whDelivered = webhookLogs.filter((l) => l.status === 'DELIVERED' || l.status === 'delivered').length;
  const whRead = webhookLogs.filter((l) => l.status === 'READ' || l.status === 'read').length;
  const whFailed = webhookLogs.filter((l) => l.status === 'FAILED' || l.status === 'failed').length;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950">Logs</h2>
          <p className="text-sm text-foreground-500 mt-1">
            {tab === 'system' ? 'Registro de todas as atividades e eventos da plataforma' : 'Status de envio em tempo real via Webhook'}
          </p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="inline-flex bg-background-100 rounded-full p-1">
        <button onClick={() => setTab('system')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${tab === 'system' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
          <i className="ri-file-list-3-line mr-1.5" />Logs do Sistema
        </button>
        <button onClick={() => setTab('webhook')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${tab === 'webhook' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
          <i className="ri-webhook-line mr-1.5" />Logs de Envio
          {webhookLogs.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">{webhookLogs.length}</span>}
        </button>
      </div>

      {tab === 'system' && (
        <>
          {/* Category filter buttons */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {logCategories.map((cat) => {
              const isActive = category === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => { setCategory(cat.key); setPage(1); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    isActive ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`} />
                  {cat.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-[320px]">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text" value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                placeholder="Filtrar por ação ou usuário..." className="w-full pl-9 pr-4 py-2 bg-white border border-background-200 rounded-lg text-sm outline-none focus:border-primary-400 transition-all"
              />
            </div>
            <span className="text-xs text-foreground-500">{filtered.length} registros</span>
          </div>

          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase">Tipo</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase">Ação</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase">Usuário</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase">Detalhes</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase">IP</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase">Data/Hora</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500 uppercase w-12">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((log) => {
                    const catInfo = logCategories.find((c) => c.key === log.category);
                    return (
                      <tr key={log.id} className="border-b border-background-50 hover:bg-background-50 transition-colors">
                        <td className="px-5 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-background-100 text-foreground-600">
                            <i className={`${catInfo?.icon || 'ri-information-line'} text-[10px]`} />
                            {catInfo?.label || log.category}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${actionColors[log.action] || 'bg-background-200 text-foreground-600'}`}>
                            <i className={`${actionIcons[log.action] || 'ri-information-line'} text-xs`} />{log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3"><span className="text-foreground-700 font-medium text-[13px]">{log.user}</span></td>
                        <td className="px-4 py-3 text-foreground-600 text-[13px] max-w-[360px] truncate">{log.details}</td>
                        <td className="px-4 py-3"><code className="text-xs text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded font-mono">{log.ip}</code></td>
                        <td className="px-5 py-3 text-right text-foreground-500 text-[13px] whitespace-nowrap">{formatDate(log.created_at)}</td>
                        <td className="px-2 py-3 text-center">
                          <ActionMenu
                            align="right"
                            actions={[
                              { label: copiedId === log.id ? 'Copiado!' : 'Copiar detalhes', icon: copiedId === log.id ? 'ri-check-line' : 'ri-file-copy-line', iconColor: copiedId === log.id ? 'text-emerald-500' : 'text-foreground-400', onClick: () => copyToClipboard(`[${log.action}] ${log.user} — ${log.details}`, log.id) },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {paginated.length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-sm text-foreground-400">Nenhum log nesta categoria</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-background-100">
                <span className="text-xs text-foreground-500">Página {page} de {totalPages}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40">
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = i + 1;
                    return <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${p === page ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100'}`}>{p}</button>;
                  })}
                  <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40">
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'webhook' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-background-200 p-3 text-center"><p className="text-xl font-bold text-primary-600">{whSent}</p><p className="text-xs text-foreground-500">Enviados</p></div>
            <div className="bg-white rounded-lg border border-background-200 p-3 text-center"><p className="text-xl font-bold text-emerald-600">{whDelivered}</p><p className="text-xs text-foreground-500">Entregues</p></div>
            <div className="bg-white rounded-lg border border-background-200 p-3 text-center"><p className="text-xl font-bold text-accent-600">{whRead}</p><p className="text-xs text-foreground-500">Lidos</p></div>
            <div className="bg-white rounded-lg border border-background-200 p-3 text-center"><p className="text-xl font-bold text-red-500">{whFailed}</p><p className="text-xs text-foreground-500">Falhas</p></div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setAutoRefresh(!autoRefresh)} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${autoRefresh ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-background-100 border-background-200 text-foreground-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-emerald-500 animate-pulse' : 'bg-foreground-300'}`} />{autoRefresh ? 'Auto-refresh' : 'Pausado'}
              </button>
              <button onClick={() => fetchWebhookLogs()} className="flex items-center gap-1 text-xs font-medium text-foreground-500 hover:text-foreground-700 px-2.5 py-1.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-refresh-line" />Atualizar</button>
            </div>
            <span className="text-xs text-foreground-400">{webhookLogs.length} eventos</span>
          </div>

          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              {loadingWebhook && webhookLogs.length === 0 ? (
                <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
              ) : webhookLogs.length === 0 ? (
                <div className="text-center py-12"><div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4"><i className="ri-webhook-line text-2xl text-foreground-400" /></div><p className="text-sm text-foreground-500">Nenhum evento de webhook recebido ainda</p></div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-background-100"><th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase">Hora</th><th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase">Instância</th><th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase">Evento</th><th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase">Telefone</th><th className="text-center py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase">Status</th><th className="text-center py-2.5 px-2 text-xs font-semibold text-foreground-500 uppercase w-12">Ações</th></tr></thead>
                  <tbody>
                    {paginatedWebhook.map((log) => {
                      const evtColor = webhookEventColors[log.status || log.event_type] || 'bg-background-100 text-foreground-600 border-background-200';
                      const evtIcon = webhookEventIcon[log.status || log.event_type] || 'ri-information-line';
                      const statusText = webhookStatusLabel[log.status || ''] || log.event_type;
                      return (
                        <tr key={log.id} className="border-b border-background-50 hover:bg-background-50/50 transition-colors">
                          <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap font-mono">{formatShortDate(log.created_at)}</td>
                          <td className="py-2.5 px-3"><span className="text-xs text-foreground-700 font-medium">{log.instance_name}</span></td>
                          <td className="py-2.5 px-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${evtColor}`}><i className={`${evtIcon} text-[10px]`} />{log.event_type}</span></td>
                          <td className="py-2.5 px-3"><code className="text-xs text-foreground-500 font-mono">{log.phone_number ? log.phone_number.replace('@s.whatsapp.net', '') : '—'}</code></td>
                          <td className="py-2.5 px-3 text-center">{log.status ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${evtColor}`}><i className={`${evtIcon} text-[10px]`} />{statusText}</span> : <span className="text-xs text-foreground-400">—</span>}</td>
                          <td className="py-2.5 px-2 text-center">
                            <ActionMenu
                              align="right"
                              actions={[
                                { label: copiedId === log.id ? 'Copiado!' : 'Copiar dados', icon: copiedId === log.id ? 'ri-check-line' : 'ri-file-copy-line', iconColor: copiedId === log.id ? 'text-emerald-500' : 'text-foreground-400', onClick: () => copyToClipboard(`[${log.instance_name}] ${log.event_type} → ${log.phone_number?.replace('@s.whatsapp.net', '') || 'N/A'} | Status: ${log.status || 'N/A'}`, log.id) },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {webhookTotalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-background-100">
                <span className="text-xs text-foreground-500">Página {webhookPage} de {webhookTotalPages}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setWebhookPage(Math.max(1, webhookPage - 1))} disabled={webhookPage === 1} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"><i className="ri-arrow-left-s-line" /></button>
                  <button onClick={() => setWebhookPage(Math.min(webhookTotalPages, webhookPage + 1))} disabled={webhookPage === webhookTotalPages} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"><i className="ri-arrow-right-s-line" /></button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}